import classNames from 'classnames';
import { CircleCheckBig, CircleX, Info, Trash2, TriangleAlert, X } from 'lucide-react';
import type { HTMLAttributes } from 'react';
import { ToastContainer as ToastContainerPrimitive, type ToastContentProps, toast } from 'react-toastify';
import { Icon } from '../Icon';
import * as styles from './index.css';

export function ToastContainer() {
    return (
        <ToastContainerPrimitive
            position="bottom-right"
            hideProgressBar
            toastClassName={styles.toastReset}
            closeButton={false}
        />
    );
}

type ToastData = {
    title: string;
    description: string;
};

type ToastMode = 'success' | 'error' | 'warning' | 'info';

const toastConfig: Record<
    ToastMode,
    { containerClass: string; iconClass: string; descriptionClass: string; icon: typeof CircleCheckBig }
> = {
    success: {
        containerClass: styles.successToast,
        iconClass: styles.successToastIcon,
        descriptionClass: styles.successToastDescription,
        icon: CircleCheckBig,
    },
    error: {
        containerClass: styles.errorToast,
        iconClass: styles.errorToastIcon,
        descriptionClass: styles.errorToastDescription,
        icon: CircleX,
    },
    warning: {
        containerClass: styles.warningToast,
        iconClass: styles.warningToastIcon,
        descriptionClass: styles.warningToastDescription,
        icon: TriangleAlert,
    },
    info: {
        containerClass: styles.infoToast,
        iconClass: styles.infoToastIcon,
        descriptionClass: styles.infoToastDescription,
        icon: Info,
    },
};

type ToastProps = ToastContentProps & {
    data: ToastData & {
        mode: ToastMode;
    };
};

function Toast({ closeToast, data }: ToastProps) {
    const { mode, title, description } = data;
    const { containerClass, iconClass, descriptionClass, icon } = toastConfig[mode];

    return (
        <div className={containerClass}>
            <Icon icon={icon} size="md" className={iconClass} />
            <p className={styles.toastContent}>
                <span className={styles.toastTitle}>{title}</span>
                <span className={descriptionClass}>{description}</span>
            </p>
            <button type="button" onClick={closeToast} className={styles.closeButton}>
                <Icon icon={X} size="sm" />
            </button>
        </div>
    );
}

export const showSuccessToast = (data: ToastData) =>
    toast(Toast, { data: { mode: 'success', ...data }, className: styles.container });

export const showErrorToast = (data: ToastData) =>
    toast(Toast, { data: { mode: 'error', ...data }, autoClose: false, className: styles.container });

export const showWarningToast = (data: ToastData) =>
    toast(Toast, { data: { mode: 'warning', ...data }, className: styles.container });

export const showInfoToast = (data: ToastData) =>
    toast(Toast, { data: { mode: 'info', ...data }, className: styles.container });

type EmbeddedToastProps = HTMLAttributes<HTMLDivElement> & {
    title: string;
    message: string | string[];
};

export function EmbeddedWarningToast({ title, message, ...props }: EmbeddedToastProps) {
    return (
        <div className={classNames(toastConfig.warning.containerClass, styles.embeddedToastContainer)} {...props}>
            <Icon icon={TriangleAlert} size="md" />
            <p className={styles.toastContent}>
                <span className={styles.toastTitle}>{title}</span>
                <span className={styles.toastDescription}>{Array.isArray(message) ? message.join('\n') : message}</span>
            </p>
        </div>
    );
}

export function EmbeddedDangerToast({ title, message, ...props }: EmbeddedToastProps) {
    return (
        <div className={classNames(toastConfig.error.containerClass, styles.embeddedToastContainer)} {...props}>
            <Icon icon={Trash2} size="md" />
            <p className={styles.toastContent}>
                <span className={styles.toastTitle}>{title}</span>
                <span className={styles.toastDescription}>{Array.isArray(message) ? message.join('\n') : message}</span>
            </p>
        </div>
    );
}
